import { Suspense, useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import useModal from '@carvana/ds-modal';
import { ErrorFilledCvna } from '@carvana/ds-icons';
import { ChevronRight, AlertTriangle } from 'lucide-react';
import { breakpoint, icon } from '@carvana/ds-styles';
import Banner from '@carvana/ds-banner';
import Button from '@carvana/ds-button';
import Skeleton from '@carvana/ds-skeleton';
import SummaryTile from './SummaryTile';
import {
  StyledBanner,
  TileCard,
  ImageWrapper,
  StyledLinkButton,
  TileActionModalContent,
  TileActionModalTitle,
  TileActionModalBody,
  TileActionModalButtons
} from './SummaryTiles.styles';
import styles from './SummaryTiles.module.css';
import { removeVehicleConfirmation } from './SummaryTiles.content';
import { useRemoveVehicleMutation } from './hooks';
import { LifecycleState, type VehicleSummary } from 'api/GraphQL/queries/getVehicleSummaries';
import useImageQuery from 'shared/hooks/useImageQuery';
import useAnalyticEvent from 'shared/hooks/useAnalyticEvent';
import { useMediaQuery } from 'shared/hooks/useMediaQuery';

const triggerImageLoader = ({ src, width }: { src: string; width: number }) =>
  src.includes('?') ? `${src}&w=${width}` : `${src}?w=${width}`;

const coverStyle = { objectFit: 'cover' as const };

type SummaryTilesModalProps = {
  summaries: VehicleSummary[];
};

// Loading placeholder for the modal content
const ModalContentSkeleton = () => (
  <>
    <TileCard $size="md">
      <Skeleton width="100%" height="120px" />
    </TileCard>
    <TileCard $size="md">
      <Skeleton width="100%" height="120px" />
    </TileCard>
  </>
);

const SummaryTilesModal = ({ summaries }: SummaryTilesModalProps) => {
  const { Modal, openModal } = useModal();
  const {
    Modal: ConfirmModal,
    openModal: openConfirmModal,
    closeModal: closeConfirmModal
  } = useModal();
  const { recordActionClicked } = useAnalyticEvent();
  const tileCount = summaries.length + 1;

  const [pendingAcquisitionName, setPendingAcquisitionName] = useState<string | null>(null);

  const {
    mutate: mutateRemoveVehicle,
    isPending,
    isError: isMutationError,
    reset: resetRemoveVehicle
  } = useRemoveVehicleMutation(() => {
    closeConfirmModal();
    setPendingAcquisitionName(null);
  });

  const handleVehicleAction = useCallback(
    (acquisitionName: string) => {
      resetRemoveVehicle();
      setPendingAcquisitionName(acquisitionName);
      openConfirmModal();
    },
    [openConfirmModal, resetRemoveVehicle]
  );

  const handleConfirmClose = useCallback(() => {
    closeConfirmModal();
    setPendingAcquisitionName(null);
    resetRemoveVehicle();
  }, [closeConfirmModal, resetRemoveVehicle]);

  const handleConfirm = useCallback(() => {
    if (!pendingAcquisitionName) return;
    mutateRemoveVehicle(pendingAcquisitionName);
  }, [pendingAcquisitionName, mutateRemoveVehicle]);

  const handleOpen = async () => {
    await recordActionClicked(
      'Landing Page - View your other vehicles',
      'View your other vehicles tile click',
      { summaryCount: tileCount },
      true
    );
    openModal();
  };

  // Fetch initial vehicle image
  const { year, make, model, trim, color } = summaries[0];
  const yearMakeModel = `${year} ${make} ${model}`;
  const { data: imageUrl } = useImageQuery({
    year: year || 0,
    make: make || '',
    model: model || '',
    trim: trim || '',
    color: color || '',
    isMobile: true
  });

  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const showImage = !!imageUrl && !imageError;

  useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
  }, [imageUrl]);

  // Fallback to no-image variant if image doesn't load within 5 seconds
  useEffect(() => {
    if (!imageUrl || imageLoaded || imageError) return undefined;
    const timeout = setTimeout(() => setImageError(true), 5000);
    return () => clearTimeout(timeout);
  }, [imageUrl, imageLoaded, imageError]);

  const isMobile = useMediaQuery(`(max-width: ${breakpoint.sm})`);

  return (
    <>
      <TileCard onClick={handleOpen} $size="sm">
        <ImageWrapper $hasImage={showImage} $size={isMobile ? 'sm' : 'md'} $overrideImageBackground>
          {showImage && (
            <Image
              loader={triggerImageLoader}
              src={imageUrl}
              alt={yearMakeModel}
              width={isMobile ? 48 : 64}
              height={isMobile ? 38 : 54}
              style={coverStyle}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          )}
        </ImageWrapper>
        <StyledLinkButton size={isMobile ? 'sm' : 'md'} onClick={handleOpen} underline={false}>
          View Your {summaries.length} Other Vehicles
          <ChevronRight color={icon.primary} size={isMobile ? 16 : 24} />
        </StyledLinkButton>
      </TileCard>
      <Modal
        modalHeader="Your other vehicles"
        desktopWidth="480px"
        classes={{ header: styles.modalHeader }}
        onClose={handleConfirmClose}
        disableOutsideClose={!!pendingAcquisitionName}
      >
        <Suspense fallback={<ModalContentSkeleton />}>
          {summaries.map(summary => (
            <SummaryTile
              key={summary.appraisalId}
              summary={summary}
              isMobile
              tileCount={tileCount}
              showTileActions={summary.lifecycleState === LifecycleState.Expired}
              onVehicleAction={handleVehicleAction}
            />
          ))}
        </Suspense>
      </Modal>
      {/* Renders as a separate portal stacked on top of the tile list modal.
          Both use @carvana/ds-modal so they share the same globalPortalRegistry,
          meaning clicks inside this modal are recognised by the outer modal's
          shouldIgnoreOutsideClick — the outer modal stays open. */}
      <ConfirmModal
        desktopWidth="480px"
        zIndex={10000000}
        onClose={handleConfirmClose}
        divLine={false}
      >
        <TileActionModalContent>
          <StyledBanner
            inline
            type="critical"
            open={isMutationError}
            icon={<ErrorFilledCvna size={40} color={icon.critical} />}
          >
            <Banner.Message>
              <strong>Something went wrong, please try again.</strong>
            </Banner.Message>
          </StyledBanner>
          <AlertTriangle size={40} color={icon.primary} />
          <TileActionModalTitle>{removeVehicleConfirmation.title}</TileActionModalTitle>
          <TileActionModalBody>{removeVehicleConfirmation.body}</TileActionModalBody>
        </TileActionModalContent>
        <TileActionModalButtons>
          <Button
            size="md"
            width="100%"
            variant="primary"
            onClick={handleConfirm}
            disabled={isPending}
          >
            {removeVehicleConfirmation.confirmText}
          </Button>
          <Button
            size="md"
            width="100%"
            variant="tertiary"
            onClick={handleConfirmClose}
            disabled={isPending}
          >
            Cancel
          </Button>
        </TileActionModalButtons>
      </ConfirmModal>
    </>
  );
};

export default SummaryTilesModal;
